
/**
 * Module dependencies.
 */

import { CollectionProps } from 'src/types/collection';
import { absoluteUrlResolver } from 'src/core/utils/url-resolver';
import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { resolvePageUrl, routeTemplate } from 'src/core/utils/routes';
import { routes } from 'src/core/routes';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import CollectionCard from 'src/components/cards/collection-card';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import React, { Fragment, ReactElement, useEffect, useMemo } from 'react';
import Type from 'src/components/core/typography';
import map from 'lodash/map';
import styled, { createGlobalStyle, css } from 'styled-components';
import useCollections from 'src/api/app/collections/use-collections';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';

/**
 * Default locale.
 */

const defaultLocale: string = process.env.NEXT_PUBLIC_DEFAULT_LOCALE;


/**
 * ISO locales.
 */

const isoLocales = process.env.NEXT_PUBLIC_LOCALE_ISO_CODES.split(',').reduce((acc, curr) => {
  const key = curr.split('_')[0];
  acc[key] = curr;

  return acc;
}, {});

/**
 * `PageProps` type.
 */

type PageProps = {
  description?: string | null,
  descriptionSeo?: string | null,
  id: string,
  slug: string,
  subtitle: string,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * Export `Props` type.
 */

export type Props = {
  collections: {
    pageParams: number[],
    pages: Array<{
      data: CollectionProps[],
      nextPage: number | null,
      total: number
    }>
  },
  data: PageProps,
  isKiosk?: boolean,
  locale: string,
  slug: string
};

/**
 * `GlobalStyle` component.
 */

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${color('beige100')};
  }
`;

/**
 * `Section` styled component.
 */

const Section = styled.section<{
  isKiosk?: boolean,
  isLoading: boolean
}>`
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(10)};
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}

  ${ifProp('isKiosk', css`
    padding-bottom: ${units(19)};
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title    .'
      '. subTitle .'
      '. list     .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H3).attrs({ as: 'h1' })`
  grid-area: title;
  padding-top: ${units(10)};

  span {
    display: block;
  }
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H6).attrs({
  as: 'p'
})`
  grid-area: subTitle;
  margin-bottom: ${units(6)};
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.section`
  grid-area: list;
  min-height: 250px;
  position: relative;
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  text-align: center;
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  display: grid;
  grid-gap: ${units(4)};
  grid-template-columns: 100%;

  ${media.min('xs')`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('md')`
    grid-template-columns: repeat(3, 1fr);
  `}
`;

/**
 * `LoadMore` styled component.
 */

const LoadMore = styled.div`
  padding: ${units(13)} 0;
  text-align: center;
`;

/**
 * `ApplicationPage` component.
 */

const ApplicationPage = (props: Props): ReactElement => {
  const { translate } = useTranslate();
  const { locales, query } = useRouter();

  const { data, locale } = props;

  const {
    data: collectionsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess
  } = useCollections({
    filterParams: query,
    initialData: props?.collections
  });

  const isUpdating = isFetchingNextPage || isFetching;
  const total = collectionsData?.pages[0]?.total ?? 0;

  useNavbarTheme({ theme: 'dark' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [query]);

  const hrefLangs = useMemo(() => {
    return locales.map(locale => {
      const slug = data?.translations.find((translation) => translation.locale === isoLocales[locale])?.slug as string;
      const route = routeTemplate(routes[locale].application, { slug });

      return {
        lang: locale,
        url: defaultLocale === locale ? absoluteUrlResolver(`${route}`) : absoluteUrlResolver(`/${locale}${route}`)
      };
    });
  }, [data?.translations, locales]);
  
  const canonicalUrl = `${resolvePageUrl('applications', locale)}/${data.slug}`;

  return (
    <>
      <Metatags
        canonicalUrl={canonicalUrl}
        description={data?.descriptionSeo}
        hrefLangs={hrefLangs}
        isKiosk={props?.isKiosk}
        pageTitle={data?.title}
        title={data?.titleSeo}
      />

      <GlobalStyle />

      <Section
        isKiosk={props?.isKiosk}
        isLoading={isLoading}
      >
        <Loading active={isLoading} />

        <Container>
          <Grid>
            <Title>
              <FadeInUpAnimation>
                {`${data?.title} `}
              </FadeInUpAnimation>
            </Title>

            {data?.description && (
              <SubTitle>
                <FadeInUpAnimation>
                  {`${data?.description} `}
                </FadeInUpAnimation>
              </SubTitle>
            )}

            {isSuccess && (
              <ListWrapper>
                <Loading active={isLoading || isUpdating} />

                {!isLoading && !isUpdating && total === 0 && (
                  <Empty>
                    <Type.H6 paddingVertical={units(1)}>
                      <FadeInUpAnimation>
                        {translate('collections:list.emptyList')}
                      </FadeInUpAnimation>
                    </Type.H6>
                  </Empty>
                )}

                {total > 0 && (
                  <>
                    <List>
                      {map(collectionsData?.pages, (collections, index: number) => (
                        <Fragment key={index}>
                          {map(collections?.data, (collection: CollectionProps, collectionIndex: number) => (
                            <FadeInUpAnimation key={collectionIndex}>
                              <CollectionCard
                                isKioskCard={props?.isKiosk}
                                key={collection.id}
                                {...collection}
                              />
                            </FadeInUpAnimation>
                          ))}
                        </Fragment>
                      ))}
                    </List>

                    {hasNextPage && (
                      <FadeInUpAnimation>
                        <LoadMore>
                          <Button
                            disabled={isUpdating}
                            isLoading={isUpdating}
                            onClick={() => fetchNextPage()}
                          >
                            {translate('common:actions.loadMore')}
                          </Button>
                        </LoadMore>
                      </FadeInUpAnimation>
                    )}
                  </>
                )}
              </ListWrapper>
            )}
          </Grid>
        </Container>
      </Section>
    </>
  );
};

/**
 * Export `ApplicationPage` component.
 */

export default ApplicationPage;
